'use client'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { IconContext } from '@phosphor-icons/react'
import { useState, useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { ClerkProvider, useUser } from '@clerk/nextjs'
import { ActiveThemeProvider } from '@/components/theme/active-theme'
import { useTheme } from 'next-themes'
import { dark } from '@clerk/themes'
import { identifyUser, resetUser, optOutIfInternal } from '@/lib/analytics'
import { ServiceWorkerRegister } from '@/components/pwa/service-worker-register'
import { InstallPrompt } from '@/components/pwa/install-prompt'
import { ClerkAnonRecorder, PostHogAnonRecorder } from '@/components/posthog-anon-recorder'
import { ICON_DEFAULTS } from '@/components/icons/icon-defaults'

function PostHogIdentify() {
  const { user, isSignedIn } = useUser()
  const identified = useRef(false)

  useEffect(() => {
    if (isSignedIn && user && !identified.current) {
      optOutIfInternal(user.id)
      identifyUser(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
        name: user.fullName,
      })
      identified.current = true
    }

    if (!isSignedIn && identified.current) {
      resetUser()
      identified.current = false
    }
  }, [isSignedIn, user])

  return null
}

export function Providers({
  activeThemeValue,
  isSignedIn,
  children,
}: {
  activeThemeValue: string
  /** From the server's auth(); the landing page needs Clerk on the client only for signed-in visitors. */
  isSignedIn: boolean
  children: React.ReactNode
}) {
  // we need the resolvedTheme value to set the baseTheme for clerk based on the dark or light theme
  const { resolvedTheme } = useTheme()
  const pathname = usePathname()

  // Signed-out visitors on the landing page get no ClerkProvider, so clerk-js (~300KB from
  // Clerk's CDN) never loads there. The page's CTAs come from the server's auth(). Signed-in
  // visitors keep it, so their session token stays fresh for the jump to the app. Once
  // mounted it stays mounted: it only goes away on the landing page's first load.
  const clerkNeeded = isSignedIn || pathname !== '/'
  const [clerkMounted, setClerkMounted] = useState(clerkNeeded)
  if (clerkNeeded && !clerkMounted) setClerkMounted(true)

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // With SSR, we usually want to set some default staleTime
            // above 0 to avoid refetching immediately on the client
            staleTime: 60 * 1000,
          },
        },
      })
  )

  const app = (
    <QueryClientProvider client={queryClient}>
      <IconContext.Provider value={ICON_DEFAULTS}>
        <ServiceWorkerRegister />
        <InstallPrompt />
        {children}
      </IconContext.Provider>
    </QueryClientProvider>
  )

  return (
    <ActiveThemeProvider initialTheme={activeThemeValue}>
      {clerkMounted ? (
        <ClerkProvider
          appearance={{
            baseTheme: resolvedTheme === 'dark' ? dark : undefined,
          }}
        >
          <PostHogIdentify />
          <ClerkAnonRecorder />
          {app}
        </ClerkProvider>
      ) : (
        <>
          <PostHogAnonRecorder signedOut />
          {app}
        </>
      )}
    </ActiveThemeProvider>
  )
}
